<template>
  <div class="chat-container card sidebar" :class="{ small: !fullView }">
    <div v-show="fullView" class="top-part">
      <img class="themed-logo" :src="brandLogo" />
      <div style="cursor: pointer" @click="closeSidebar()">
        <b-icon icon="close" />
      </div>
    </div>
    <b-tabs v-show="fullView" v-model="activeTab" :class="{ 'first-survey-tab': event.survey }">
      <b-tab-item v-if="event.survey" value="survey">
        <template slot="header">
          <b-icon icon="chart-pie" />
        </template>
        <template>
          <div class="sidebar-padding">
            <SurveyChat :survey-code="event.survey.code" @new-question="openSidebar(), (activeTab = 'survey')" />
          </div>
        </template>
      </b-tab-item>
      <b-tab-item value="description" :visible="!isViewerLive">
        <template slot="header">
          <b-icon icon="format-align-left" />
        </template>
        <template>
          <div class="sidebar-padding" style="max-width: calc(100%)">
            <h1 class="event-title">
              {{ event.name }}
            </h1>
            <viewer v-if="event.description" :initial-value="event.description" />
          </div>
        </template>
      </b-tab-item>
      <b-tab-item value="chat">
        <template slot="header">
          <b-icon icon="forum" :class="newMsg ? 'red-bubble' : ''" />
        </template>
        <template>
          <div class="flex-outer-container">
            <div class="vertical-flex">
              <div v-if="!isViewerLive" class="chat-option">
                <b-field :style="{ marginLeft: chatOptionMargin + 'px' }">
                  <b-select v-model="channel" placeholder="Select a Chat" expanded>
                    <option value="main">
                      {{ $t("publicChat") }}
                    </option>
                    <option v-if="isTranslator" value="trans">
                      {{ $t("translatorChat") }}
                    </option>
                    <option
                      v-if="isTranslator"
                      :value="[clientTranslator.lang1, clientTranslator.lang2].sort().join('-')"
                    >
                      {{ [clientTranslator.lang1, clientTranslator.lang2].sort().join("-").toUpperCase() }}
                    </option>
                  </b-select>
                </b-field>
                <b-tooltip
                  v-if="isModerator"
                  class="delete-chat-button"
                  :label="$t('deleteChatButton')"
                  position="is-right"
                  type="is-primary"
                  animated
                  multilined
                >
                  <b-button
                    type="is-danger is-light"
                    icon-right="delete"
                    icon
                    @click="confirmDeleteChatHistory"
                  ></b-button>
                </b-tooltip>
                <b-tooltip
                  v-if="isModerator"
                  class="download-chat-button"
                  :label="$t('downloadChatButton')"
                  position="is-right"
                  type="is-primary"
                  animated
                  multilined
                >
                  <b-button icon-right="download" icon @click="exportMessagesAsCsv"></b-button>
                </b-tooltip>
              </div>
              <div
                v-chat-scroll="{
                  always: false,
                  smooth: true,
                  notSmoothOnInit: true,
                }"
                class="chat-messages my-2"
              >
                <div v-if="moreMessages" class="has-text-centered" @click="loadMore">
                  <b>{{ $t("loadMore") }}</b>
                </div>
                <div v-for="msg in outputMessages" :key="msg.id" :class="{ 'pm-message': msg.pm, 'any-message': true }">
                  <b-tooltip
                    :label="msg.timestamp.toDate().toLocaleTimeString()"
                    position="is-right"
                    type="is-dark"
                    animated
                  >
                    <b style="display: inline-block; cursor: pointer" @click="openPm(users[msg.userId])">
                      {{ users[msg.userId] ? users[msg.userId].name : msg.userId }}
                    </b>
                    <span v-if="msg.pm">
                      <b-icon icon="chevron-right" size="is-small" />
                      <b>{{
                        users[msg.channel.find((c) => c != msg.uid)]
                          ? users[msg.channel.find((c) => c != msg.uid)].name
                          : ""
                      }}</b>
                    </span>
                  </b-tooltip>
                  <span v-if="msg.file">
                    <br />
                    <a target="_blank" :href="msg.file.url">
                      <b-button
                        type="is-primary is-light"
                        class="file-btn mt-1 mb-2"
                        :icon-left="mapTypeToIcon(msg.file.type)"
                        >{{ msg.file.name }}</b-button
                      >
                    </a>
                  </span>
                  <span v-else v-linkified v-html="'&nbsp;' + msg.body" />
                </div>
              </div>

              <div class="input-container">
                <b-input
                  v-model="messageInput"
                  style="flex-grow: 1"
                  type="text"
                  :loading="isSending"
                  @keyup.enter.native="sendMessage(messageInput)"
                />
              </div>
              <div class="input-container">
                <b-dropdown v-if="isTranslator" id="tt-step-4" aria-role="list" position="is-top-left">
                  <button slot="trigger" slot-scope="{ active }" class="button mr-1">
                    <b-icon :icon="active ? 'menu-down' : 'menu-up'" />
                  </button>

                  <b-dropdown-item aria-role="listitem" @click="sendAllPartners('Please take over')">
                    Please take over
                  </b-dropdown-item>
                  <b-dropdown-item aria-role="listitem" @click="sendAllPartners(`AFK for 2 Minutes`)">
                    AFK for 2 Minutes
                  </b-dropdown-item>
                  <b-dropdown-item aria-role="listitem" @click="sendMessage('Speaker is too fast')">
                    Speaker is too fast
                  </b-dropdown-item>
                </b-dropdown>
                <UploadFile @fileUploaded="sendUploadedFile">
                  <a id="tt-step-5" class="button mr-1">
                    <b-icon icon="upload" />
                  </a>
                </UploadFile>
                <b-button type="is-primary" icon-left="send" :loading="isSending" @click="sendMessage(messageInput)" />
              </div>
            </div>
          </div>
        </template>
      </b-tab-item>
      <b-tab-item value="participants" :visible="!isViewerLive">
        <template slot="header">
          <b-icon icon="account-multiple" />
        </template>
        <template>
          <div class="sidebar-padding">
            <div v-if="isModerator" class="mb-3">
              <div class="red-title mb-2">
                {{ $t("allParticipants") }}
              </div>
              <b-button
                type="is-danger"
                size="is-small"
                icon-left="microphone-off"
                class="mr-1"
                @click="
                  sortedParticipants.forEach((u) => {
                    if (u.micPaused === false && u.id !== mediasoupId) {
                      $emit('global-mute', u.id);
                    }
                  })
                "
              >
                MUTE ALL
              </b-button>
              <b-button
                type="is-danger"
                size="is-small"
                icon-left="video-off"
                class="mr-1"
                @click="
                  sortedParticipants.forEach((u) => {
                    if (u.vidPaused === false && u.id !== mediasoupId) {
                      $emit('global-video-mute', u.id);
                    }
                  })
                "
              >
                ALL VIDEOS OFF
              </b-button>
              <b-button
                type="is-danger"
                size="is-small"
                icon-left="video-off"
                class="mr-1"
                @click="$emit('stop-presentation')"
              >
                Stop Presentation
              </b-button>
              <!-- <b-button
                type="is-danger"
                size="is-small"
                icon-left="video-off"
                class="mr-1"
                @click="$emit('toggle-resolution')"
              >
                Toggle Resolution
              </b-button> -->
            </div>
            <div class="green-title">
              {{ $t("participants") }}
            </div>
            <div v-for="u in sortedParticipants" :key="u.id" class="participant-container">
              <div class="participant">
                <div>
                  <div class="name" @click="openPm(firebaseUser(u.id))">
                    {{ u.displayName }}
                  </div>
                  <div class="lang">
                    {{ u.produceLang.toLowerCase() != "zxx" ? u.produceLang : "" }}
                  </div>
                </div>
                <div class="">
                  <div v-if="true" class="subtitle is-6" style="font-size: 0.7em; display: flex">
                    <div v-if="u.handRaised" @click="$emit('disable-hand-raised', u.id)">
                      <b-icon icon="hand-right" style="color: #ffdd57" class="ml-1" />
                    </div>
                    <div
                      v-if="isModerator && u.id != $store.getters.getCurrentUser.uid && u.role !== 'viewer'"
                      @click="$emit('switch-to-viewer', u.id)"
                    >
                      <b-tooltip :label="$t('switchUserToViewer')" position="is-left" animated>
                        <b-icon icon="ear-hearing" class="ml-1" />
                      </b-tooltip>
                    </div>
                    <div
                      v-if="!(u.role === 'viewer' || u.role === 'proxy')"
                      :class="{ 'icons-red': u.micPaused }"
                      @click="$emit('global-mute', u.id)"
                    >
                      <b-tooltip :label="!u.micPaused ? $t('mute') : ''" position="is-left" animated>
                        <b-icon :icon="!u.micPaused ? 'microphone' : 'microphone-off'" />
                      </b-tooltip>
                    </div>
                    <div
                      v-if="!(u.role === 'viewer' || u.role === 'proxy')"
                      :class="{ 'icons-red': u.vidPaused }"
                      @click="$emit('global-video-mute', u.id)"
                    >
                      <b-tooltip :label="!u.vidPaused ? $t('muteVideo') : ''" position="is-left" animated>
                        <b-icon :icon="!u.vidPaused ? 'video' : 'video-off'" class="ml-1" />
                      </b-tooltip>
                    </div>
                    <div
                      v-if="u.id != $store.getters.getCurrentUser.uid && u.role === 'viewer'"
                      :class="{
                        'icons-red': u.reqToSpeakOngoing,
                        'icons-blue': u.allowedToSpeak,
                      }"
                      @click="$emit('allow-switch-to-host', u.id)"
                    >
                      <b-icon v-if="!u.reqToSpeakOngoing" icon="account-voice" class="ml-1" />
                      <b-tooltip v-if="u.reqToSpeakOngoing" :label="$t('allowToSpeak')" position="is-left" animated>
                        <b-icon v-if="u.reqToSpeakOngoing" icon="account-voice" class="ml-1" />
                      </b-tooltip>
                    </div>
                    <div
                      v-if="
                        isModerator && u.role !== 'viewer' // && u.id != $store.getters.getCurrentUser.uid
                      "
                      @click="emitSpotlightEvent(u.id)"
                    >
                      <b-tooltip :label="$t('spotlight')" position="is-left" animated>
                        <b-icon
                          icon="spotlight"
                          :class="{
                            'ml-1': true,
                            'icons-red': u.id === spotlightPeerId,
                          }"
                        />
                      </b-tooltip>
                    </div>
                    <div v-if="isModerator && u.id != $store.getters.getCurrentUser.uid" @click="$emit('kick', u.id)">
                      <b-tooltip :label="$t('kick')" position="is-left" animated>
                        <b-icon icon="close-circle-outline" class="ml-1" />
                      </b-tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <!--
              <div
                v-if="isModerator && u.id != $store.getters.getCurrentUser.uid"
                class="user-list-mod-settings mt-1"
              >
                <b-button
                  v-if="u.reqSwitchToHost"
                  type="is-success"
                  size="is-small"
                  icon-left="check"
                  class="mr-1"
                  @click="$emit('allow-switch-to-host', u.id)"
                >
                  ALLOW TO SPEAK
                </b-button>
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="logout"
                  class="mr-1"
                  :disabled="!u.online"
                  @click="$emit('kick', u.id)"
                >
                  BACK TO AUDIENCE
                </b-button>
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="logout"
                  @click="$emit('leave', u.id)"
                >
                  KICK
                </b-button>
              </div>-->
            </div>
            <div class="green-title" style="margin-top: 42px">
              {{ $t("translators") }}
            </div>
            <div
              v-for="t in sortedTranslators"
              :key="t.id"
              class="participant participant-container"
              @click="openPm(firebaseUser(t.id))"
            >
              <div>
                <div class="name">
                  {{ t.displayName }}
                </div>
                <div class="lang">
                  <span
                    :class="{
                      black:
                        t.clientTranslator.lang1.toUpperCase() == t.clientTranslator.currentOutputLang.toUpperCase(),
                    }"
                    >{{ t.clientTranslator.lang1 }}</span
                  >
                  <b-icon class="mx-0" size="is-small" :icon="t.mono ? 'arrow-right-bold' : 'arrow-left-right-bold'" />
                  <span
                    :class="{
                      black:
                        t.clientTranslator.lang2.toUpperCase() == t.clientTranslator.currentOutputLang.toUpperCase(),
                    }"
                    >{{ t.clientTranslator.lang2 }}</span
                  >
                  &nbsp;<span v-if="t.clientTranslator.translatorReady == true"
                    ><b-icon class="mx-0" size="is-small" style="color: #ccff23" icon="check" /> READY</span
                  >
                </div>
              </div>
              <div>
                <b-button
                  class="on-air user-list"
                  :class="{
                    active:
                      t.onAir ||
                      ($store.getters.getCurrentUser &&
                        $store.getters.getCurrentUser.uid == t.id &&
                        localMute == false),
                  }"
                >
                  ON AIR
                </b-button>
              </div>
            </div>
          </div>
        </template>
      </b-tab-item>
      <b-tab-item value="settings" :visible="!isViewerLive">
        <template slot="header">
          <b-icon icon="cog" />
        </template>
        <template>
          <div class="sidebar-padding">
            <div v-if="localAudioTrack">
              <b-field :label="$t('audio')">
                <b-tooltip :label="$t('audioSelectDesc')" position="is-top" multilined animated>
                  <b-select
                    v-model="micId"
                    placeholder="Select a device"
                    :loading="loadingIndicator"
                    :disabled="loadingIndicator"
                  >
                    <option v-for="mic in microphones" :key="mic.deviceId" :value="mic.deviceId">
                      {{ mic.label }}
                    </option>
                  </b-select>
                </b-tooltip>
              </b-field>
            </div>
            <div v-if="localVideoTrack" class="mt-3">
              <b-field :label="$t('video')">
                <b-tooltip :label="$t('cameraSelectDesc')" position="is-top" multilined animated>
                  <b-select
                    v-model="camId"
                    placeholder="Select a device"
                    :loading="loadingIndicator"
                    :disabled="loadingIndicator"
                  >
                    <option v-for="cam in cameras" :key="cam.deviceId" :value="cam.deviceId">
                      {{ cam.label }}
                    </option>
                  </b-select>
                </b-tooltip>
              </b-field>
            </div>
            <!-- <div v-if="!isTranslator && !isModerator" class="mt-4">
              <b-button @click="clickMod">
                {{ $t("becomeMod") }}
              </b-button>
            </div> -->
            <div v-if="!isTranslator" class="my-2">
              <b-tooltip :label="$t('volSliderOrigSound')" position="is-top" animated multilined>
                <b-field class="vol-slider">
                  <!--{{ $t('hearOriginal') }} -->
                  {{ $t("volumeOrigSound") }}
                  <b-slider v-model="floorVolume" />
                </b-field>
              </b-tooltip>
              <br />
              <b-tooltip
                v-if="translatorsOnAir"
                :label="$t('volSliderSelectedLang')"
                position="is-top"
                animated
                multilined
              >
                <b-field class="vol-slider">
                  <!--{{ $t('hearOriginal') }} -->
                  {{ $t("volumeSelectedLang") }}
                  <b-slider v-model="consumeVolume" />
                </b-field>
              </b-tooltip>
              <b-tooltip :label="$t('presentationVol')" position="is-top" animated multilined>
                <b-field class="vol-slider" :label="$t('presentationVol')">
                  <b-slider v-model="presentationVolume" />
                </b-field>
              </b-tooltip>
            </div>
            <div v-if="isTranslator" class="my-2">
              <b-tooltip :label="$t('presentationVol')" position="is-top" animated multilined>
                <b-field class="vol-slider" :label="$t('presentationVol')">
                  <b-slider v-model="presentationVolume" />
                </b-field>
              </b-tooltip>
            </div>
            <div v-if="isTranslator" class="mt-3">
              <b-field v-for="(l, i) in settingsRelay" :key="i" :label="$t('relay') + ' ' + (i + 1)" class="mt-3">
                <b-tooltip :label="$t('relay')" position="is-top" animated>
                  <b-select
                    v-model="settingsRelay[i]"
                    placeholder="Select a device"
                    :loading="loadingIndicator"
                    :disabled="loadingIndicator"
                  >
                    <option v-for="lw in langWhiteList" :key="lw" :value="lw">
                      {{ lw.toUpperCase() }}
                    </option>
                  </b-select>
                </b-tooltip>
              </b-field>
            </div>
            <div v-if="!isTranslator && role !== 'hybrid'" class="mt-3">
              <b-field :label="$t('changeLang')" class="mt-3">
                <b-select
                  :placeholder="consumeLang"
                  :loading="loadingIndicator"
                  :disabled="loadingIndicator"
                  @input="emitConsumeLangChanged"
                >
                  <option v-for="lw in langWhiteList" :key="lw" :value="lw">
                    {{ lw.toUpperCase() }}
                  </option>
                </b-select>
              </b-field>
              <b-field :label="$t('interpretation')" class="mt-3">
                <b-tooltip :label="$t('noInterpretationNeededTip')" position="is-top" animated multilined>
                  <b-button
                    :outlined="noTranslation"
                    :type="noTranslation ? 'is-danger' : ''"
                    @click="toggleNoTranslation"
                    >{{ $t("noInterpretationNeeded") }}</b-button
                  >
                </b-tooltip>
              </b-field>
              <!-- Uncomment to activate subtitle -->
              <!-- <b-field :label="$t('subtitle')" class="mt-3">
                <b-button
                  v-if="!transcriptionStarted"
                  outlined
                  type="is-primary"
                  @click="transcriptionOnClicked"
                >
                  {{ $t("turnOnSubtitle") }}
                </b-button>
                <b-button
                  v-if="transcriptionStarted"
                  outlined
                  type="is-primary"
                  @click="transcriptionOffClicked"
                >
                  {{ $t("turnOffSubtitle") }}
                </b-button>
              </b-field> -->
            </div>
          </div>
        </template>
      </b-tab-item>
    </b-tabs>
    <b-modal :active.sync="pmOpen" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <PrivateMessageModal
        :participant="currentPmParticipant"
        :messages-collection="messagesCollection"
        :cipher="cipher"
      />
    </b-modal>
    <div v-show="!fullView" class="small-container">
      <div>
        <div class="pointer" @click="openSidebar()">
          <b-icon icon="menu" />
        </div>
      </div>
      <div>
        <div v-if="event.survey && !isViewerLive" class="pointer" @click="openSidebar(), (active = 'survey')">
          <b-icon icon="chart-pie" />
        </div>
        <div v-if="!isViewerLive" class="pointer" @click="openSidebar(), (activeTab = 'description')">
          <b-icon icon="format-align-left" />
        </div>
        <div class="pointer" @click="openSidebar(), (activeTab = 'chat')">
          <b-icon icon="forum" :class="newMsg ? 'red-bubble' : ''" />
        </div>
        <div v-if="!isViewerLive" class="pointer" @click="openSidebar(), (activeTab = 'participants')">
          <b-icon icon="account-multiple" />
        </div>
        <div v-if="!isViewerLive" class="pointer" @click="openSidebar(), (activeTab = 'settings')">
          <b-icon icon="cog" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/no-parsing-error */
/* eslint-disable vue/valid-v-html */
/**
 * Event Chat module for room.
 * Provides options, settings, chatting etc.
 * @displayName EventChat
 */
import { FieldValue, db } from "@/firebase";
import aes256 from "aes256";
import UploadFile from "./UploadFile";
import PrivateMessageModal from "./PrivateMessageModal";
import { Viewer } from "@toast-ui/vue-editor";
//import Vue from 'vue'
import SurveyChat from "@/components/survey/SurveyChat";
import { mapState } from "vuex";

export default {
  name: "EventChat",
  components: {
    UploadFile,
    PrivateMessageModal,
    Viewer,
    SurveyChat,
  },
  props: {
    /**
     * Firebase event object.
     */
    event: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * Translators collection from firebase.
     */
    translators: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * Participants collection from firebase.
     * Participants are users who are not translators.
     */
    participants: {
      type: Object,
      default() {
        return {};
      },
    },
    agoraUsers: {
      type: Array,
      default() {
        return [];
      },
    },
    localMute: {
      type: Boolean,
      default() {
        return true;
      },
    },
    localAudioTrack: {
      type: Object,
      default() {
        return {};
      },
    },
    localVideoTrack: {
      type: Object,
      default() {
        return {};
      },
    },
    currentMicId: {
      type: String,
      default() {
        return "";
      },
    },
    currentCamId: {
      type: String,
      default() {
        return "";
      },
    },
    microphones: {
      type: Array,
      default() {
        return [];
      },
    },
    cameras: {
      type: Array,
      default() {
        return [];
      },
    },
    loadingIndicator: {
      type: Boolean,
      default: false,
    },
    isTranslator: {
      type: Boolean,
      default: false,
    },
    settingsRelay: {
      type: Array,
      default() {
        return [];
      },
    },
    langWhiteList: {
      type: Array,
      default() {
        return [];
      },
    },
    clientTranslator: {
      type: Object,
      default() {
        return {};
      },
    },
    translatorPartners: {
      type: Array,
      default() {
        return [];
      },
    },
    isModerator: {
      type: Boolean,
      default: false,
    },
    hearOriginal: {
      type: Number,
      default: 20,
    },
    big: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fullView: this.big,
      messageInput: "",
      channel: "main",
      isSending: false,
      isDeletingMessages: false,
      messages: [],
      limit: 50,
      console: console,
      pmOpen: false,
      currentPmParticipant: null,
      activeTab: "chat",
      lastReadMsg: null,
      timeHack: 0,
      chatOptionMargin: this.isModerator ? "88" : "0",
    };
  },
  computed: {
    ...mapState({
      peers: (state) => state.mediasoup.peers,
      micPaused: (state) => state.mediasoup.micPaused,
      vidPaused: (state) => state.mediasoup.vidPaused,
      mediasoupId: (state) => state.mediasoup.mediasoupId,
      role: (state) => state.mediasoup.role,
      produceLang: (state) => state.mediasoup.produceLang,
      consumeLang: (state) => state.mediasoup.consumeLang,
      spotlightPeerId: (state) => state.mediasoup.spotlightPeerId,
      isPresentation: (state) => state.mediasoup.isPresentation,
      noTranslation: (state) => state.mediasoup.noTranslation,
      userName: (state) => state.mediasoup.getDisplayName,
      isViewerLive: (state) => state.mediasoup.isViewerLive,
      translatorsOnAir: (state) => state.mediasoup.translatorsOnAir,
      transcriptionStarted: (state) => state.mediasoup.transcriptionStarted,
    }),
    brandLogo() {
      if (this.event.branding.logoUrl) {
        return this.event.branding.logoUrl;
      }
      return "/assets/qondalogo.svg";
    },
    amISpotlighted() {
      if (this.mediasoupId === this.spotlightPeerId) {
        return true;
      } else {
        return false;
      }
    },
    floorVolume: {
      get() {
        return this.$store.getters["mediasoup/floorVolume"];
      },
      set(value) {
        this.$store.commit("mediasoup/set_floor_volume", value);
      },
    },
    consumeVolume: {
      get() {
        return this.$store.getters["mediasoup/consumeVolume"];
      },
      set(value) {
        this.$store.commit("mediasoup/set_consume_volume", value);
      },
    },
    presentationVolume: {
      get() {
        return this.$store.getters["mediasoup/presentationVolume"];
      },
      set(value) {
        this.$store.commit("mediasoup/set_presentation_volume", value);
      },
    },
    consumeOrFloorVol: {
      get() {
        if (this.translatorsOnAir) {
          return this.consumeVolume;
        } else {
          return this.floorVolume;
        }
      },
      set(value) {
        if (this.translatorsOnAir) {
          this.$store.commit("mediasoup/set_consume_volume", value);
        } else {
          this.$store.commit("mediasoup/set_floor_volume", value);
        }
      },
    },
    newMsg() {
      try {
        return this.lastReadMsg != this.outputMessages[this.outputMessages.length - 1].id;
      } catch (err) {
        return false;
      }
    },
    micId: {
      get() {
        return this.currentMicId;
      },
      set(val) {
        this.$emit("change-mic", val);
      },
    },
    camId: {
      get() {
        return this.currentCamId;
      },
      set(val) {
        this.$emit("change-webcam", val);
      },
    },
    users() {
      let participants = this.participants;
      let translators = this.translators;
      return { ...participants, ...translators };
    },
    cipher() {
      return aes256.createCipher(this.$route.params.encryptionPw);
    },
    messagesCollection() {
      return db
        .collection("organizations")
        .doc(this.$route.params.organizationId)
        .collection("events")
        .doc(this.$route.params.eventId)
        .collection("messages");
    },
    participantsCollection() {
      return db
        .collection("organizations")
        .doc(this.$route.params.organizationId)
        .collection("events")
        .doc(this.$route.params.eventId)
        .collection("participants");
    },
    outputMessages() {
      return this.decryptedMessages.map((m) => {
        try {
          return {
            ...m,
            body: m.body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"),
            pm: m.channel.includes(m.userId) && m.channel.includes(this.$store.getters.getCurrentUser.uid),
            file: m.body.match(/^\/\/file\s/g) ? JSON.parse(m.body.replace(/^\/\/file\s/g, "")) : undefined,
          };
        } catch (err) {
          return {
            ...m,
            body: m.body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"),
          };
        }
      });
    },
    decryptedMessages() {
      return this.messages
        .map((m) => ({
          ...m,
          body: this.cipher.decrypt(m.body),
          id: m.id,
          ".key": m.id,
        }))
        .reverse();
    },
    moreMessages() {
      return this.limit < this.decryptedMessages.length;
    },
    messagesQuery() {
      return this.messagesCollection
        .where("channel", "array-contains-any", [this.channel, this.$store.getters.getCurrentUser.uid])
        .orderBy("timestamp", "desc")
        .limit(this.limit + 1);
    },
    sortedParticipants() {
      let allPeers = this.$store.getters["mediasoup/getAllPeers"];
      const participants = allPeers.filter((peer) => {
        if (peer.role !== "translator" && peer.role !== "listener" && peer.role !== "proxy") {
          return true;
        }
      });

      // soritng the participants based on handRaised and reqToSpeakOngoing
      let sorted = participants.sort((a, b) => {
        if (
          (a.reqToSpeakOngoing || a.handRaised || b.reqToSpeakOngoing || b.handRaised) &&
          !((a.reqToSpeakOngoing || a.handRaised) && (b.reqToSpeakOngoing || b.handRaised))
        ) {
          return a.reqToSpeakOngoing || a.handRaised ? -1 : 1;
        } else {
          return 0;
        }
      });
      console.log("Filesorted participants", sorted);
      return sorted;
    },

    sortedTranslators() {
      let allPeers = this.$store.getters["mediasoup/getAllPeers"];
      const translators = allPeers.filter((peer) => {
        if (peer.role === "translator") {
          return true;
        }
      });

      // sorting the translators
      let sorted = translators.map((translator) => {
        if (this.mediasoupId !== translator.id) {
          return {
            ...translator,
            self: false,
          };
        } else {
          translator.micPaused = this.micPaused;
          translator.vidPaused = this.vidPaused;
          return {
            ...translator,
            self: true,
          };
        }
      });
      console.log("sorted translators", sorted);
      return sorted;
    },
  },
  watch: {
    outputMessages() {
      this.updateReadMsg();
    },
    activeTab() {
      this.updateReadMsg();
    },
    fullView(val) {
      this.$emit("update:big", val);
      this.updateReadMsg();
    },
    settingsRelay(val) {
      this.$emit("update:settingsRelay", val);
    },
    channel() {
      this.$bind("messages", this.messagesQuery);
    },
    hearOriginal(val) {
      this.$emit("update:hearOriginal", val);
    },
  },
  created() {
    setInterval(() => {
      this.timeHack = this.timeHack + 1;
    }, 5000);
  },
  methods: {
    transcriptionOnClicked() {
      this.$store.commit("mediasoup/set_transcriptionOnClicked", true);
    },
    transcriptionOffClicked() {
      this.$store.commit("mediasoup/set_transcriptionOffClicked", true);
    },
    // consumeOrFloorVol() {
    //   if (this.translatorsOnAir) {
    //     return this.consumeVolume;
    //   } else {
    //     return this.floorVolume;
    //   }
    // },
    firebaseUser(mediasoupId) {
      // Converting to an array for filtering with mediasoupId
      const userArray = Object.values(this.users);
      const user = userArray.filter((user) => {
        return user.mediasoupId === mediasoupId;
      });
      return user[0];
    },
    emitConsumeLangChanged(val) {
      this.$emit("changed-consume-lang", val);
    },
    emitSpotlightEvent(mediasoupId) {
      console.log("spotlight peerid", mediasoupId);
      if (!this.isPresentation) {
        this.$emit("spotlight", mediasoupId);
      } else {
        this.$buefy.toast.open({
          message: this.$t("cannotSpotlight"),
          type: "is-danger",
        });
      }
    },
    disableHandRaised() {
      alert("hand raise disabled");
    },
    toggleNoTranslation() {
      if (this.noTranslation) {
        this.$store.commit("mediasoup/set_noTranslation", false);
      } else {
        this.$store.commit("mediasoup/set_noTranslation", true);
      }
    },
    async clickMod() {
      this.$buefy.dialog.prompt({
        message: this.$t("whatsTheModPw"),
        trapFocus: true,
        onConfirm: async (value) => {
          let snap = await db
            .doc(
              `/organizations/${this.$route.params.organizationId}/events/${this.$route.params.eventId}/data/moderationPw`,
            )
            .get();
          if (snap.exists && snap.data().moderationPw == value) {
            this.$buefy.toast.open({
              message: this.$t("modPwSuccess"),
              type: "is-success",
            });
            this.$emit("become-mod");
          } else {
            this.$buefy.toast.open({
              message: this.$t("modPwWrong"),
              type: "is-danger",
            });
          }
        },
      });
    },
    updateReadMsg() {
      if ((this.activeTab == "chat" || this.activeTab == 1) && this.fullView) {
        try {
          this.lastReadMsg = this.outputMessages[this.outputMessages.length - 1].id;
        } catch (err) {
          console.log(err);
        }
      }
    },
    sendAllPartners(msg) {
      this.translatorPartners.forEach((t) => {
        this.messagesCollection.add({
          userId: this.$store.getters.getCurrentUser.uid,
          userName: this.$store.getters["mediasoup/getDisplayName"],
          channel: [t.id, this.$store.getters.getCurrentUser.uid],
          body: this.cipher.encrypt((this.messageInput.length == 0 ? " " : "") + msg),
          timestamp: FieldValue.serverTimestamp(),
        });
      });
    },
    sendMessage(msg) {
      this.isSending = true;
      if (msg) {
        this.messageInput = msg;
      }
      this.messagesCollection
        .add({
          userId: this.$store.getters.getCurrentUser.uid,
          userName: this.$store.getters["mediasoup/getDisplayName"],
          channel: [this.channel],
          body: this.cipher.encrypt((this.messageInput.length == 0 ? " " : "") + this.messageInput),
          timestamp: FieldValue.serverTimestamp(),
        })
        .then(() => {
          this.messageInput = "";
        })
        .finally(() => {
          this.isSending = false;
        });
    },
    confirmDeleteChatHistory() {
      this.$buefy.dialog.confirm({
        title: "Delete chat history?",
        message:
          "Are you sure you want to <b>delete</b> the whole chat history for this event? This action cannot be undone.",
        confirmText: this.$t("delete"),
        cancelText: this.$t("cancel"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          await this.deleteAllMessages();
          this.$buefy.toast.open({
            message: this.$t("chatHistoryDeleted"),
            type: "is-danger",
          });
        },
      });
    },
    async deleteAllMessages() {
      this.isDeletingMessages = true;

      this.messagesCollection
        .get()
        .then((collection) => {
          collection.forEach((doc) => {
            doc.ref.delete();
          });
        })
        .finally(() => {
          this.isDeletingMessages = false;
        });
    },
    exportMessagesAsCsv() {
      let msgs = [];
      this.messagesCollection
        .get()
        .then((collectionSnapshot) => {
          collectionSnapshot.forEach((doc) => {
            msgs.push(doc.data());
          });
        })
        .finally(() => {
          // Converting to csv format
          const csvString = [
            ["User ID", "User Name", "Message", "Timestamp"],
            ...msgs.map((item) => [
              item.userId,
              item.userName,
              this.cipher.decrypt(item.body),
              item.timestamp.toDate().toLocaleTimeString(),
            ]),
          ]
            .map((e) => e.join(","))
            .join("\n");

          let csvContent = "data:text/csv;charset=utf-8," + csvString;
          let encodedUri = encodeURI(csvContent);
          let link = document.createElement("a");
          link.setAttribute("href", encodedUri);
          link.setAttribute("download", "msgs.csv");
          document.body.appendChild(link); // for firefox needed
          link.click();
        });
    },
    sendUploadedFile(file) {
      console.log(file);
      this.messageInput = `//file ${JSON.stringify(file)}`;
      this.sendMessage();
      this.activeTab = "chat";
    },
    mapTypeToIcon(type) {
      if (type) {
        return (
          [
            { icon: "file-image-outline", regex: /^image/g },
            { icon: "file-pdf-outline", regex: /pdf$/g },
            { icon: "file-powerpoint-outline", regex: /mspowerpoint$/g },
            { icon: "file-table-outline", regex: /msexcel$/g },
            { icon: "file-word-outline", regex: /msword$/g },
            { icon: "file-document-outline", regex: /^text/g },
          ].find((x) => type.match(x.regex)) || { icon: "file-document-outline" }
        ).icon;
      }
    },
    loadMore() {
      this.limit += 50;
      this.$bind("messages", this.messagesQuery);
    },
    openPm(participant) {
      this.currentPmParticipant = participant;
      this.pmOpen = true;
    },
    switchToTab(label) {
      this.activeTab = label;
    },
    openSidebar() {
      this.fullView = true;
    },
    closeSidebar() {
      this.fullView = false;
    },
  },
  firestore() {
    return {
      messages: this.messagesQuery,
    };
  },
};
</script>

<style scoped>
.participant .mdi-24px.mdi::before {
  font-size: 20px;
}

.force-takeover-btn {
  position: absolute;
  left: 78px;
  margin-top: 2px;
  max-width: 120px;
  font-size: 12px;
  font-weight: bold;
  color: #ff0082;
  padding: 8px 6px;
  line-height: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

button.button.on-air {
  height: 36px;
  padding-left: 0.2em;
  padding-right: 0.2em;
  border: 2px solid #e5e5e5;
  color: #e5e5e5;
  font-weight: bold;
  margin-top: 0;
  margin-right: 0;
}

button.button.on-air.active {
  color: #ff0082;
  border-color: #ff0082;
}

.pointer {
  cursor: pointer;
}

.small-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 8px 0;
}

.small-container .pointer {
  padding: 8px 0;
}

.green {
  color: #ccff23;
}

.black {
  color: #333333;
}

.modal {
  z-index: 700000000;
}

.spot-light {
  color: #ff0082;
}

.sidebar {
  color: #333333;
  /* transition: min-width 1s ease; */
}

.sidebar.small {
  width: 64px;
  min-width: 64px !important;
}
.sidebar.small .force-takeover-btn {
  display: none;
}

.sidebar nav.tabs {
  margin-right: 5px;
  margin-bottom: 0px;
}

.participant .name {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 24px;
}

.participant .lang {
  color: #cccccc;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 18px;
  text-transform: uppercase;
}

.pm-message {
  color: var(--primary);
  font-style: italic;
}

.any-message {
  overflow-wrap: break-word;
}

.chat-container {
  min-width: 360px;
  height: auto;
  max-height: 100%;
  z-index: 300;
}

.flex-outer-container {
  height: auto;
  max-height: 100%;
  display: flex;
}

.file-btn {
  max-width: 100%;
}

.file-btn:not(.icon) {
  display: inline-block;
  max-width: calc(100% - 24px);
  text-overflow: ellipsis;
  overflow: hidden;
}

.chat-messages {
  flex-grow: 1;
  margin-left: 2px;
  overflow: auto;
  /* for Firefox */
  min-height: 0;
  /* set to some transparent color */
  border-color: rgba(0, 0, 0, 0);
  /* here we make the color transition */
  transition: border-color 0.25s ease;
  /* make this element do the scrolling */
  overflow: auto;
}
.chat-messages:hover {
  border-color: rgba(0, 0, 0, 0.45);
}
.chat-messages::-webkit-scrollbar {
  width: 7px;
  height: 0;
}
.chat-messages::-webkit-scrollbar-thumb {
  /*background-color: rgba(0,0,0,0.0);*/
  /* add border to act as background-color */
  border-right-style: inset;
  /* sum viewport dimensions to guarantee border will fill scrollbar */
  border-right-width: calc(100vw + 100vh);
  /* inherit border-color to inherit transitions */
  border-color: inherit;
  border-radius: 10px;
  box-shadow: rgba(255, 255, 255, 0.3) 0 0 0 1px;
  transition: background-color 0.5s ease;
}

.input-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 3px;
}

.vertical-flex {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1em 10px;
}

.chat-option {
  display: inline-grid;
  position: relative;
}

.delete-chat-button {
  position: absolute;
}

.download-chat-button {
  position: absolute;
  margin-left: 44px;
}

.sidebar .top-part {
  margin: 32px 15px 20px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar .themed-logo {
  height: 53px;
}
</style>

<style>
.participant .mdi-24px.mdi::before {
  font-size: 20px;
}

.first-survey-tab ul li:first-child {
  position: absolute;
  left: 24px;
}

.dialog {
  z-index: 4000;
}

.sidebar .tab-item {
  /* set to some transparent color */
  border-color: rgba(0, 0, 0, 0);
  /* here we make the color transition */
  transition: border-color 0.25s ease;
  /* make this element do the scrolling */
}
.sidebar .tab-item:hover {
  border-color: rgba(0, 0, 0, 0.45);
}
.sidebar .tab-item::-webkit-scrollbar {
  width: 7px;
  height: 0;
}
.sidebar .tab-item::-webkit-scrollbar-thumb {
  /*background-color: rgba(0,0,0,0.0);*/
  /* add border to act as background-color */
  border-right-style: inset;
  /* sum viewport dimensions to guarantee border will fill scrollbar */
  border-right-width: calc(100vw + 100vh);
  /* inherit border-color to inherit transitions */
  border-color: inherit;
  border-radius: 10px;
  box-shadow: rgba(255, 255, 255, 0.3) 0 0 0 1px;
  transition: background-color 0.5s ease;
}

.red-bubble .mdi::after {
  content: "";
  position: absolute;
  width: 14px;
  height: 14px;
  background: #ff0082;
  border-radius: 20px;
  margin-top: 1px;
  margin-left: -10px;
}

.sidebar .participant {
  width: 100%;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar .participant-container {
  border-bottom: 2px solid #e5e5e5;
  padding: 16px 0;
  width: 100%;
}

.participant .icons-red .mdi {
  color: #ff0082;
}

.participant .icons-blue .mdi {
  color: #42a1f5;
}

.sidebar .red-title {
  color: #ff0082;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 24px;
}

.sidebar .green-title {
  color: var(--link);
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 24px;
}

.sidebar .event-title {
  font-size: 24px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 33px;
}

.sidebar .sidebar-padding {
  padding: 16px 32px;
  width: 100%;
}

.sidebar .tab-content {
  padding: 0;
  height: calc(100% - 10px);
}

.sidebar .tab-item {
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar .tabs ul {
  border-bottom: none;
  justify-content: flex-end;
}

.sidebar .tabs li a {
  border-bottom-color: transparent;
  padding-left: 2px;
  padding-right: 2px;
}

.sidebar .tabs li.is-active a {
  border-bottom-color: transparent;
  color: var(--link);
}

.sidebar .b-tabs {
  width: 100%;
  height: calc(100% - 123px);
}

.sidebar nav.tabs {
  margin-right: 5px;
  margin-bottom: 0px;
}

.mdi-alert-octagon::before {
  color: red;
}

.vol-slider {
  width: 250px;
}
</style>
